// 🔧 pending_events_list.js
// ✅ Purpose: Admin-only page to view and manage unarchived pending event requests
// ✅ Features:
// - Loads unarchived documents from /pendingEvents
// - Displays each event with Edit and Archive buttons
// - Edit goes to pending_events.js
// - Archive sets archived:true
// - Uses showStatus helper with type: "success" or "error"
// - Displays standardized alert boxes
// - Admin-only with RoleGuard

import React, { useEffect, useState } from 'react';
import { collection, getDocs, updateDoc, query, where, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import RoleGuard from '../../role_guard';
import { useRole } from '../../role_context';
import { showStatus } from '../../utils/status_helpers';
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from '../../utils/logger';

const PendingEventsList = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const { role } = useRole();
  const navigate = useNavigate();

  useEffect(() => {
    if (!status) return;

    const timer = setTimeout(() => {
      setStatus(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

  // 🔄 Load unarchived events
  useEffect(() => {
    if (role !== 'admin') return;

    const fetchEvents = async () => {
      try {
        const q = query(collection(db, 'pendingEvents'), where('archived', '==', false));
        const snapshot = await getDocs(q);
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        data.sort((a, b) => {
          const dateA =
            a?.createdAt?.toDate?.()?.getTime() || 0;

          const dateB =
            b?.createdAt?.toDate?.()?.getTime() || 0;

          return dateB - dateA;
        });
        setEvents(data);
      } catch (err) {
        logger.error('❌ Error loading pending events:', {
          value: err
        });
        showStatus(setStatus, { type: 'error', message: '❌ Failed to load events.' });
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [role]);

  // 🗃️ Archive a pending event
  const handleArchive = async (eventId) => {

    if (
      !window.confirm(
        "Archive this pending event request?"
      )
    ) {
      return;
    }

    try {
      await updateDoc(doc(db, 'pendingEvents', eventId), { archived: true });
      setEvents(prev => prev.filter(ev => ev.id !== eventId));
      showStatus(setStatus, { type: 'success', message: '✅ Event archived successfully.' });
    } catch (err) {
      logger.error('❌ Error archiving event:', {
        value: err
      });
      showStatus(setStatus, { type: 'error', message: '❌ Failed to archive event.' });
    }
  };

  // 🔐 Gate access to admins only
  if (role !== 'admin') return <p>🚫 Access denied</p>;
  if (loading) return <FullPageSpinner message="Loading pending events..." />;

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="row mb-4">
        <div className="col text-start">
          <h1 className="display-5">🕒 Pending Event Requests</h1>
          <p className="lead text-muted">Review events that were submitted for approval</p>
        </div>
      </div>

      <div className="white-section p-4 rounded shadow-sm bg-white">
        {status?.type === "success" && <div className="alert alert-success">{status.message}</div>}
        {status?.type === "error" && <div className="alert alert-danger">{status.message}</div>}

        {events.length === 0 ? (
          <p className="text-muted">No pending events to display.</p>
        ) : (
          <div className="table-responsive">
            <table className="table table-bordered align-middle">
              <thead className="table-light">
                <tr>
                  <th>Event Name</th>
                  <th>Voting Type</th>
                  <th>Description</th>
                  <th>Event Date</th>
                  <th>Requester</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => {
                  const requester = event.requester || {};
                  return (
                    <tr key={event.id}>
                      <td>{event.name || "Untitled"}</td>
                      <td>
                        {(event.votingType || "standard")
                          .replaceAll("_", " ")
                          .replace(/\b\w/g, (c) => c.toUpperCase())}
                      </td>
                      <td>{event.description || "—"}</td>
                      <td>
                        {event.startDate?.toDate
                          ? event.startDate.toDate().toLocaleDateString()
                          : "—"}
                      </td>
                      <td>
                        {requester.firstName || "Unknown"} {requester.lastName || ""} ({requester.email || "no email"})
                      </td>
                      <td className="d-flex gap-2">
                        <button
                          onClick={() => navigate(`/pending_event/${event.id}`)}
                          className="btn btn-sm btn-outline-primary"
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => handleArchive(event.id)}
                          className="btn btn-sm btn-outline-danger"
                        >
                          🗃️ Archive
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};

export default () => (
  <RoleGuard requiredRole="admin">
    <PendingEventsList />
  </RoleGuard>
);
